/**
 * Dynamic User Manual Generation Engine for EdVergenceX Platform
 * Automatically analyzes application routes, navigation sidebars, role permissions,
 * UI components, actions, forms, tables, charts, and workflows to generate
 * comprehensive live documentation without hardcoded static pages.
 */

// Master Registry of Platform Modules & Features
const MODULE_REGISTRY = [
  // --- ADMIN MODULES ---
  {
    key: "admin_dashboard",
    title: "Executive Dashboard & Ecosystem Overview",
    category: "Administration & Analytics",
    icon: "LayoutDashboard",
    allowedRoles: ["admin"],
    route: "/admin-dashboard",
    overview: "Central command center for administrators providing real-time platform ecosystem statistics, funnel metrics, pending approval queues, and system activity logs.",
    purpose: "Empowers executive administrators to monitor system health, track university and professor onboarding funnels, and oversee platform activity from a single dashboard.",
    actions: [
      { name: "Refresh Data", description: "Re-fetches real-time ecosystem statistics and database metrics." },
      { name: "Filter Funnel", description: "Filters user conversion funnels by date range, role, or institution type." },
      { name: "View Detail Modals", description: "Opens deep-dive inspection windows for pending applications or user registrations." },
      { name: "Export Analytics", description: "Downloads aggregated executive reports in CSV/Excel format." }
    ],
    forms: [
      { field: "Date Range Selector", type: "Dropdown / Date Picker", description: "Filters overall platform metrics by today, last 7 days, 30 days, or custom date range." },
      { field: "Ecosystem Search", type: "Search Bar", description: "Quickly searches across universities, professors, students, and system transactions." }
    ],
    tables: [
      { name: "Recent System Activity", columns: ["Timestamp", "User", "Action", "Module", "IP Address", "Status"], features: ["Search", "Filter", "Pagination", "Sorting"] },
      { name: "Pending Approvals Queue", columns: ["ID", "Name", "Role", "Applied Date", "Vetting Score", "Action"], features: ["Approve", "Reject", "View Details"] }
    ],
    charts: [
      { name: "User Growth & Onboarding Funnel", metrics: ["Applications Received", "Vetting Passed", "Approved Users", "Active Subscriptions"], calculation: "Real-time count of database registrations grouped by status.", interpretation: "Higher conversion indicates streamlined onboarding; drops signal approval bottlenecks." }
    ],
    workflow: [
      "Access Admin Dashboard",
      "Review Ecosystem Stats & System Alerts",
      "Inspect Pending Approval Queue",
      "Execute Batch/Individual Actions (Approve/Reject)",
      "Audit Activity Logs & Export Financial Reports"
    ],
    permissions: {
      allowed: ["admin", "super_admin"],
      restricted: ["student", "faculty", "university", "tpo", "pop"],
      dependencies: "Must possess valid JWT token with role === 'admin'."
    },
    tips: "Regularly check the Pending Approvals Queue to prevent onboarding delays for universities and Professors of Practice.",
    bestPractices: "Export monthly financial and activity summaries for compliance records.",
    commonErrors: [
      { error: "403 Unauthorized Access", resolution: "Ensure your account has Admin privileges. Relogin if session token has expired." },
      { error: "Metrics Loading Timeout", resolution: "Verify database connection and clear browser local storage cache." }
    ],
    faqs: [
      { q: "How often are dashboard metrics refreshed?", a: "Dashboard metrics update in real-time on page load and every 5 minutes automatically." },
      { q: "Can I customize the charts displayed?", a: "Yes, use the metric filter toggles above each chart section." }
    ]
  },
  {
    key: "admin_user_management",
    title: "User Management & Role Access Control",
    category: "Administration & Analytics",
    icon: "Users",
    allowedRoles: ["admin"],
    route: "/admin-dashboard?tab=users",
    overview: "Comprehensive user account administration module allowing admins to inspect, activate, deactivate, assign custom roles, and manage permissions for all registered platform accounts.",
    purpose: "Maintains platform security, manages user identity life cycles, and enforces Role-Based Access Control (RBAC) across Students, Faculty, TPOs, POPs, and University Admins.",
    actions: [
      { name: "Create User", description: "Registers a new user account manually with assigned role and credentials." },
      { name: "Edit User Profile", description: "Updates user name, email, phone, designation, or institutional affiliation." },
      { name: "Deactivate / Restore User", description: "Toggles user active state to suspend or reinstate platform access." },
      { name: "Assign Custom Role", description: "Grants granular permissions and custom role tags to institutional users." },
      { name: "Reset Password / Send OTP", description: "Triggers password reset or OTP verification emails to the user." }
    ],
    forms: [
      { field: "Full Name", type: "Text Input", description: "User's complete legal name." },
      { field: "Email Address", type: "Email Input", description: "Unique primary email used for login and notifications." },
      { field: "User Role", type: "Dropdown", description: "Selects user role (Student, Faculty, University, TPO, POP, Admin)." },
      { field: "Status Toggle", type: "Radio / Switch", description: "Sets account state to Active or Suspended." }
    ],
    tables: [
      { name: "All Registered Users", columns: ["ID", "Name", "Email", "Role", "Status", "Joined Date", "Actions"], features: ["Search", "Filter by Role", "Pagination", "Bulk Deactivate", "Export CSV"] }
    ],
    charts: [
      { name: "User Role Composition", metrics: ["Student Ratio", "Faculty Count", "POP Experts", "Institutional Admins"], calculation: "Percentage of total active users categorized by assigned role.", interpretation: "Highlights platform adoption and user diversity." }
    ],
    workflow: [
      "Navigate to Admin -> User Management",
      "Filter or Search for specific User",
      "Select Action (Edit, Role Assign, Deactivate)",
      "Confirm Changes in Modal",
      "System applies RBAC & logs Audit Entry"
    ],
    permissions: {
      allowed: ["admin"],
      restricted: ["student", "faculty", "university", "tpo", "pop"],
      dependencies: "Admin privilege required."
    },
    tips: "Use Bulk Actions when deactivating multiple accounts during institutional graduation cycles.",
    bestPractices: "Always verify user institutional email before assigning University or TPO administrator roles.",
    commonErrors: [
      { error: "Email Already Exists", resolution: "Each user email must be unique. Update the existing account or use a different email." }
    ],
    faqs: [
      { q: "What happens when a user is deactivated?", a: "Deactivated users cannot log in or access any API endpoints until restored by an Admin." }
    ]
  },
  {
    key: "admin_vetting",
    title: "Professor of Practice (POP) Vetting & Applications",
    category: "Vetting & Compliance",
    icon: "Award",
    allowedRoles: ["admin"],
    route: "/vetting-page",
    overview: "Rigorous vetting management interface for evaluating industry experts applying to become Professors of Practice.",
    purpose: "Ensures only qualified industry leaders with verified experience, credentials, and conflict-free backgrounds are approved to teach students.",
    actions: [
      { name: "Review Application", description: "Opens complete application folder including resume, experience certificates, and domain expertise." },
      { name: "Approve Vetting", description: "Grants POP status and activates expert profile in public discovery catalog." },
      { name: "Request Clarification / More Info", description: "Sends notification to applicant requesting additional documents or details." },
      { name: "Reject Application", description: "Rejects application with detailed feedback and rejection reasons." }
    ],
    forms: [
      { field: "Vetting Score", type: "Number Input (0-100)", description: "Assigned quality score based on industry tenure and publications." },
      { field: "Admin Remarks", type: "Text Area", description: "Official notes and feedback provided to the applicant." }
    ],
    tables: [
      { name: "POP Vetting Pipeline", columns: ["Applicant Name", "Domain Expertise", "Years Experience", "Current Org", "Vetting Stage", "Score", "Actions"], features: ["Stage Filter", "Search", "Sorting", "Export"] }
    ],
    charts: [
      { name: "Vetting Qualification Funnel", metrics: ["Submitted", "Under Review", "Doc Verified", "Interview Passed", "Approved"], calculation: "Count of applications across vetting stages.", interpretation: "Measures quality of incoming expert talent." }
    ],
    workflow: [
      "Select Candidate Application from Vetting Queue",
      "Inspect Experience, Patents, Publications & Certifications",
      "Conduct Compliance & Conflict Check",
      "Assign Vetting Score & Add Remarks",
      "Click Approve or Reject -> Automatic Email Sent"
    ],
    permissions: {
      allowed: ["admin"],
      restricted: ["student", "faculty", "university", "tpo", "pop"],
      dependencies: "Requires Admin Role."
    },
    tips: "Verify candidate LinkedIn and organizational profile before approving domain leadership status.",
    bestPractices: "Document specific reasons when rejecting or requesting additional information.",
    commonErrors: [
      { error: "Missing Documents Alert", resolution: "Request candidate to re-upload clear PDF copies of experience certificates." }
    ],
    faqs: [
      { q: "Can a rejected candidate re-apply?", a: "Yes, candidates can update their profile and re-submit after 30 days." }
    ]
  },
  {
    key: "admin_moderation",
    title: "Content Moderation & Policy Enforcement",
    category: "Safety & Policy",
    icon: "ShieldAlert",
    allowedRoles: ["admin"],
    route: "/admin-dashboard?tab=moderation",
    overview: "Centralized moderation hub displaying user-flagged posts, direct messages, and comments alongside automated policy violation logs.",
    purpose: "Maintains a safe, respectful, and compliant environment by enabling administrators to review and take action on reported content.",
    actions: [
      { name: "Mark Reviewed", description: "Marks report as reviewed without deleting the underlying content." },
      { name: "Dismiss Report", description: "Dismisses report if content complies with community guidelines." },
      { name: "Delete Content", description: "Purges the offensive post, comment, or message permanently from the system." },
      { name: "Delete Log", description: "Removes report log from moderation history." }
    ],
    forms: [
      { field: "Admin Note", type: "Text Area", description: "Internal notes explaining action taken on moderation item." }
    ],
    tables: [
      { name: "User Content Reports", columns: ["ID", "Content Type", "Reporter", "Author", "Snippet", "Reason", "Status", "Actions"], features: ["Sub-tab Filter", "Status Badge", "Delete Content", "Dismiss"] }
    ],
    charts: [
      { name: "Report Breakdown by Reason", metrics: ["Hate Speech", "Harassment", "Spam", "False Info", "Inappropriate"], calculation: "Aggregate counts of reported items by reason category.", interpretation: "Highlights dominant community safety concerns." }
    ],
    workflow: [
      "Open Admin Dashboard -> Moderation tab",
      "Toggle between 'User Reports' and 'Auto-Filter Violations'",
      "Inspect snippet content, author, and reported reason",
      "Click 'Delete Content' to purge item or 'Dismiss' if compliant",
      "System logs moderation outcome"
    ],
    permissions: {
      allowed: ["admin"],
      restricted: ["student", "faculty", "university", "tpo", "pop"],
      dependencies: "Admin authentication."
    },
    tips: "Deleting content automatically updates all linked user reports to ACTION_TAKEN.",
    bestPractices: "Review pending reports daily to maintain institutional safety standards.",
    commonErrors: [
      { error: "Content Already Removed", resolution: "If the author deleted the post, click 'Delete Log' to clear stale reports." }
    ],
    faqs: [
      { q: "Are deleted messages recoverable?", a: "No, deleting reported content permanently purges it from the database." }
    ]
  },
  {
    key: "admin_lms",
    title: "LMS Course Management",
    category: "Learning & Curriculum",
    icon: "BookOpen",
    allowedRoles: ["admin"],
    route: "/admin-dashboard?tab=lms",
    overview: "Centralized catalog to manage external partner courses and assign them to respective universities.",
    purpose: "Enables administrators to browse available curriculum from LMS partners and selectively provision courses to institutional tenants.",
    actions: [
      { name: "Refresh Catalog", description: "Syncs the latest course catalog from external LMS integration." },
      { name: "Assign Course to University", description: "Assigns a specific course to one or more universities." },
      { name: "Bulk Assign Courses", description: "Assigns multiple selected courses to universities in a single action." },
      { name: "View Assigned Universities", description: "Displays which universities currently have access to a specific course." }
    ],
    forms: [
      { field: "Select Universities", type: "Checkbox List", description: "Selection of target universities to receive the course assignment." }
    ],
    tables: [
      { name: "Course Catalog Grid", columns: ["Thumbnail", "Course Title", "Difficulty", "Duration", "Assigned To"], features: ["Select All", "Read More", "Assign"] }
    ],
    charts: [],
    workflow: [
      "Access Admin Dashboard -> LMS Tab",
      "Browse or Refresh the Course Catalog",
      "Select an individual course or use 'Select All' for bulk action",
      "Click 'Assign to University' or 'Bulk Assign'",
      "Select target universities from the modal list",
      "Confirm assignment to provision courses"
    ],
    permissions: {
      allowed: ["admin"],
      restricted: ["student", "faculty", "university", "tpo", "pop"],
      dependencies: "LMS API Integration must be active."
    },
    tips: "Use Bulk Assign when onboarding a new university that requires the full core curriculum.",
    bestPractices: "Periodically refresh the catalog to ensure course metadata like duration and difficulty are up to date.",
    commonErrors: [
      { error: "Assignment Failed", resolution: "Verify that the LMS external service is reachable and API tokens are valid." }
    ],
    faqs: [
      { q: "Can I remove a course assignment from a university here?", a: "Course removal is typically managed at the university tenant level or via explicit unassign endpoints." }
    ]
  },
  {
    key: "admin_licensing",
    title: "University LMS Licensing",
    category: "Administration & Analytics",
    icon: "Building2",
    allowedRoles: ["admin"],
    route: "/admin-dashboard?tab=licensing",
    overview: "Manage student enrollment licenses for partner universities.",
    purpose: "Provides a centralized interface to allocate and monitor the number of student LMS licenses granted to each university.",
    actions: [
      { name: "Edit Licenses", description: "Modifies the number of allocated licenses for a specific university." },
      { name: "Save Licenses", description: "Commits the updated license limit to the system." },
      { name: "Search Universities", description: "Quickly find a university to view or update its license quota." }
    ],
    forms: [
      { field: "Licenses Allocated", type: "Number Input", description: "The maximum number of students allowed to enroll from the university." }
    ],
    tables: [
      { name: "University Licensing Roster", columns: ["University Name", "Licenses Allocated", "Licenses Used", "Status"], features: ["Edit", "Search", "Status Badges"] }
    ],
    charts: [],
    workflow: [
      "Access Admin Dashboard -> Licensing Tab",
      "Search for the specific university",
      "Click 'Edit Licenses' to change the allocation",
      "Enter the new number and click 'Save'",
      "Monitor the Status column to see if they are 'Healthy', 'Near Limit', or 'Exceeded'"
    ],
    permissions: {
      allowed: ["admin"],
      restricted: ["student", "faculty", "university", "tpo", "pop"],
      dependencies: "Admin privilege required."
    },
    tips: "Regularly check universities marked as 'Near Limit' or 'Exceeded' to proactively negotiate license upgrades.",
    bestPractices: "Do not reduce allocated licenses below the number currently used, as this will prevent new enrollments.",
    commonErrors: [
      { error: "Failed to update licenses", resolution: "Verify database connection and valid number input." }
    ],
    faqs: [
      { q: "What happens when a university exceeds its limit?", a: "The status changes to 'Exceeded' and new students may not be able to enroll until the limit is increased." }
    ]
  },

  // --- UNIVERSITY MODULES ---
  {
    key: "uni_dashboard",
    title: "University Executive Dashboard",
    category: "Institutional Management",
    icon: "Building",
    allowedRoles: ["university", "custom_role"],
    route: "/uni-dashboard",
    overview: "Institutional dashboard for university leadership to manage faculties, students, TPOs, course engagements, placement drives, and compliance.",
    purpose: "Centralizes higher education institution operations, Industry-Academia collaborations, student placements, and departmental oversight.",
    actions: [
      { name: "Add Faculty / Student / TPO", description: "Registers institutional users individually or via bulk Excel/CSV import." },
      { name: "Book POP Session", description: "Requests an expert Professor of Practice to deliver lectures, workshops, or courses." },
      { name: "Create Placement Drive", description: "Launches new campus recruitment drives for eligible graduating batches." },
      { name: "Export Department Data", description: "Downloads CSV reports of registered students, faculty performance, and placements." }
    ],
    forms: [
      { field: "User Import File", type: "File Upload (.xlsx, .csv)", description: "Bulk user enrollment sheet following standard template columns." },
      { field: "Batch Year", type: "Dropdown", description: "Assigns students or drives to specific academic graduating years." }
    ],
    tables: [
      { name: "Departmental User Roster", columns: ["ID", "Name", "Email", "Department", "Role", "Status", "Actions"], features: ["Tab Switching", "Search", "Filter by Batch", "Excel Download"] },
      { name: "Engagements & Sessions", columns: ["Topic", "Expert POP", "Date & Time", "Students Enrolled", "Status"], features: ["Filter", "View Link", "Cancel"] }
    ],
    charts: [
      { name: "Placement & Engagement Velocity", metrics: ["Total Students", "Enrolled Courses", "Placed Students", "Industry Hours"], calculation: "Cumulative metrics calculated across institutional departments.", interpretation: "Evaluates career readiness and industry exposure." }
    ],
    workflow: [
      "Access University Dashboard",
      "Select User Category (Students, Faculty, TPO) or Engagements",
      "Add Users or Schedule POP Sessions",
      "Monitor Placement Drives & Institutional Reports"
    ],
    permissions: {
      allowed: ["university", "admin", "custom_role"],
      restricted: ["student", "faculty", "tpo", "pop"],
      dependencies: "Must belong to a verified University account."
    },
    tips: "Use the bulk import template to quickly populate hundreds of student accounts at the start of academic terms.",
    bestPractices: "Assign custom roles to department heads for delegated student approvals.",
    commonErrors: [
      { error: "Invalid Excel Format", resolution: "Download the provided Excel template and ensure column headers match exact names." }
    ],
    faqs: [
      { q: "Can a university create custom sub-roles?", a: "Yes, use the Custom Roles tab under University Management." }
    ]
  },


  // --- STUDENT MODULES ---
  {
    key: "student_dashboard",
    title: "Student Growth & Learning Dashboard",
    category: "Student Experience",
    icon: "GraduationCap",
    allowedRoles: ["student"],
    route: "/student-dashboard",
    overview: "Personalized student portal displaying enrolled courses, upcoming POP lectures, placement job opportunities, AI career guidance, and schedule.",
    purpose: "Empowers students to learn from industry experts, track skill progression, participate in discussions, and apply for campus job drives.",
    actions: [
      { name: "Enroll in Course / Session", description: "Registers for expert POP masterclasses or academic courses." },
      { name: "Apply for Job", description: "Submits resume and profile to active campus placement drives." },
      { name: "Launch AI Career Agent", description: "Opens AI assistant for resume analysis, interview prep, and skill gap checks." },
      { name: "Post in Group Discussion", description: "Shares insights, questions, or media in peer study groups." }
    ],
    forms: [
      { field: "Resume File", type: "PDF Upload", description: "Uploads student resume for job applications and AI analysis." },
      { field: "Post Creation Area", type: "Rich Text Area", description: "Creates discussion posts with mentions (@) and hashtags (#)." }
    ],
    tables: [
      { name: "My Applied Drives & Status", columns: ["Drive Title", "Company", "Applied Date", "Status", "Interview Slot"], features: ["Filter", "View Application"] }
    ],
    charts: [
      { name: "Skill Mastery & Credits", metrics: ["Completed Hours", "Skill Score", "Course Progress %", "Certificates Earned"], calculation: "Automated progress tracking based on quiz scores and lecture completions.", interpretation: "Visualizes readiness for industry placement." }
    ],
    workflow: [
      "Access Student Dashboard",
      "Review Today's Schedule & Enrolled Courses",
      "Engage in Groups / Direct Messaging",
      "Apply for Eligible Placement Drives",
      "Track Career Progress"
    ],
    permissions: {
      allowed: ["student", "admin"],
      restricted: ["faculty", "university", "tpo", "pop"],
      dependencies: "Active Student Enrollment."
    },
    tips: "Keep your profile bio and uploaded PDF resume updated to improve placement recommendations.",
    bestPractices: "Participate regularly in POP masterclass Q&A sessions to earn domain micro-credentials.",
    commonErrors: [
      { error: "Job Application Locked", resolution: "Ensure your CGPA meets the company threshold set by your TPO." }
    ],
    faqs: [
      { q: "How do I download my certificates?", a: "Go to Profile -> My Certificates to download verified PDFs." }
    ]
  },
  {
    key: "student_lms",
    title: "Learning Management System (LMS)",
    category: "Student Experience",
    icon: "BookOpen",
    allowedRoles: ["student", "faculty", "pop"],
    route: "/student-dashboard?tab=lms",
    overview: "Interactive course delivery portal featuring video lectures, downloadable slides, assignments, quizzes, and discussion forums.",
    purpose: "Delivers accredited curriculum and industry masterclasses with structured learning paths.",
    actions: [
      { name: "Watch Video Lesson", description: "Streams HD video content with auto-resume playback." },
      { name: "Submit Assignment", description: "Uploads completed project files before the specified deadline." },
      { name: "Take Quiz", description: "Completes timed knowledge evaluation assessments." }
    ],
    forms: [
      { field: "Assignment Submission", type: "File Upload / Text Area", description: "Uploads project solution files or link." }
    ],
    tables: [
      { name: "Course Curriculum Syllabus", columns: ["Module", "Lesson Title", "Duration", "Resource Type", "Completion"], features: ["Expandable Modules"] }
    ],
    charts: [
      { name: "Module Completion Progress", metrics: ["Videos Watched", "Quizzes Passed", "Average Grade %"], calculation: "Weighted average of completed course requirements.", interpretation: "Guides completion trajectory." }
    ],
    workflow: [
      "Select Enrolled Course from LMS",
      "Complete Sequential Lessons & Quizzes",
      "Upload Required Assignments",
      "Receive Grade & Final Certificate"
    ],
    permissions: {
      allowed: ["student", "faculty", "pop", "admin"],
      restricted: ["tpo"],
      dependencies: "Valid Course Enrollment."
    },
    tips: "Review lesson attachments before taking end-of-module quizzes.",
    bestPractices: "Submit assignments at least 24 hours before deadlines to allow for re-uploading if needed.",
    commonErrors: [
      { error: "File Size Limit Exceeded", resolution: "Ensure assignment file size is under 25MB or provide a Google Drive / GitHub link." }
    ],
    faqs: [
      { q: "Are lectures available offline?", a: "Lectures can be streamed anytime online; course slide PDFs are downloadable." }
    ]
  },

  // --- FACULTY MODULES ---
  {
    key: "faculty_dashboard",
    title: "Faculty Academic & Class Management",
    category: "Academic Operations",
    icon: "Users",
    allowedRoles: ["faculty"],
    route: "/faculty-dashboard",
    overview: "Academic workspace for faculty members to manage course cohorts, evaluate student submissions, track attendance, and co-teach with POP experts.",
    purpose: "Facilitates seamless academic instruction, grading, and collaboration with industry Professors of Practice.",
    actions: [
      { name: "Create Assignment", description: "Publishes new coursework, deadlines, and rubrics for enrolled students." },
      { name: "Grade Submissions", description: "Reviews student uploads, enters marks, and provides feedback." },
      { name: "Mark Attendance", description: "Records student presence for live lectures and workshops." },
      { name: "Message POP Partner", description: "Initiates direct communication with co-teaching industry expert." }
    ],
    forms: [
      { field: "Assignment Title", type: "Text Input", description: "Name of the academic coursework." },
      { field: "Max Marks", type: "Number Input", description: "Maximum score for grading." },
      { field: "Due Date & Time", type: "Date Time Picker", description: "Submission cutoff." }
    ],
    tables: [
      { name: "Student Roster & Submission Status", columns: ["Student ID", "Name", "Submission Date", "Status", "Grade", "Actions"], features: ["Grade Input", "Download Submission", "Filter Pending"] }
    ],
    charts: [
      { name: "Class Grade Distribution", metrics: ["Class Average", "Top Score", "Pass Rate %", "Pending Submissions"], calculation: "Statistical bell curve of student scores.", interpretation: "Assesses class understanding and identifies struggling students." }
    ],
    workflow: [
      "Access Faculty Dashboard",
      "Select Course & Subject",
      "Publish Assignments or Mark Attendance",
      "Evaluate Student Submissions & Provide Feedback"
    ],
    permissions: {
      allowed: ["faculty", "admin"],
      restricted: ["student", "tpo", "pop"],
      dependencies: "Faculty account assigned to institution."
    },
    tips: "Use inline feedback to help students understand score deductions.",
    bestPractices: "Coordinate with POP industry experts 3 days before guest lectures.",
    commonErrors: [
      { error: "Grades Not Saved", resolution: "Click 'Publish Grades' after entering marks to commit to database." }
    ],
    faqs: [
      { q: "Can faculty co-author courses with POP experts?", a: "Yes, use the Joint Curriculum Builder in the Course Management tab." }
    ]
  },

  // --- TPO MODULES ---
  {
    key: "tpo_dashboard",
    title: "Training & Placement Officer (TPO) Portal",
    category: "Placement Operations",
    icon: "Briefcase",
    allowedRoles: ["tpo"],
    route: "/tpo-dashboard",
    overview: "Dedicated placement management platform for TPO officers to orchestrate corporate visits, vet student resumes, organize mock interviews, and publish placement reports.",
    purpose: "Maximizes institutional placement rates and streamlines corporate recruitment logistics.",
    actions: [
      { name: "Sync Mantrika Jobs", description: "Pulls latest job roles from the Mantrika corporate partner platform." },
      { name: "Schedule Placement Drive", description: "Assigns specific interview dates and times for synced job roles." },
      { name: "Verify Student Resume", description: "Reviews and certifies student resume details before company submission." }
    ],
    tables: [
      { name: "Student Candidates", columns: ["Roll No", "Student Name", "Branch", "Resume Status", "Actions"], features: ["Search", "View Resume"] }
    ],
    charts: [
      { name: "Branch-wise Placement Rate", metrics: ["Total Eligible", "Placed", "Highest CTC", "Unplaced"], calculation: "Calculated across CS, IT, ECE, Mechanical, and Civil departments.", interpretation: "Identifies departments needing focused recruitment drives." }
    ],
    workflow: [
      "Receive Hiring Request from Enterprise HR",
      "Configure Placement Drive & Eligibility Rules",
      "Verify Student Resumes & Approve Applications",
      "Coordinate Interview Schedules",
      "Log Placement Offers & Publish Results"
    ],
    permissions: {
      allowed: ["tpo", "university", "admin"],
      restricted: ["student", "faculty", "pop"],
      dependencies: "Institutional TPO Designation."
    },
    tips: "Batch verify student resumes at the beginning of the semester to speed up drive applications.",
    bestPractices: "Maintain updated company HR contact details in the Corporate Partners directory.",
    commonErrors: [
      { error: "Unverified Resume Error", resolution: "Students cannot apply to drives until TPO clicks 'Verify Resume'." }
    ],
    faqs: [
      { q: "Can students update resumes after TPO verification?", a: "Updating a resume resets status to 'Pending Re-verification'." }
    ]
  },

  // --- POP MODULES ---
  {
    key: "pop_dashboard",
    title: "Professor of Practice (POP) Expert Portal",
    category: "Industry Expertise",
    icon: "Award",
    allowedRoles: ["pop"],
    route: "/pop-dashboard",
    overview: "Professional hub for industry leaders to offer specialized courses, accept institutional engagement invitations, conduct live masterclasses, and manage earnings.",
    purpose: "Enables top industry practitioners to share real-world expertise with universities and students while managing teaching schedules and compensation.",
    actions: [
      { name: "Accept Institutional Engagement", description: "Confirms teaching request from a university or college." },
      { name: "Create Masterclass / Course", description: "Designs syllabus, modules, and schedule for industry offering." },
      { name: "Schedule Live Session", description: "Sets up interactive Zoom/Meet webinar link for enrolled students." },
      { name: "Discover University Posts", description: "Browses and applies to academic requests posted by institutions." }
    ],
    forms: [
      { field: "Course Title", type: "Text Input", description: "Name of the industry course or masterclass." },
      { field: "Hourly Rate / Fee", type: "Currency Input", description: "Commercial compensation requirement." },
      { field: "Domain Tags", type: "Multi-select", description: "Key skill keywords (AI, Cloud, FinTech, Robotics)." }
    ],
    tables: [
      { name: "Engagement Requests Queue", columns: ["Institution", "Requested Topic", "Proposed Dates", "Students", "Offered Fee", "Status", "Actions"], features: ["Accept", "Decline", "Propose New Time"] }
    ],
    charts: [
      { name: "Teaching Hours & Rating Trends", metrics: ["Delivered Hours", "Student Rating (1-5)", "Enrolled Students", "Total Earnings"], calculation: "Aggregated ratings and payout statements.", interpretation: "Reflects POP teaching impact and institutional satisfaction." }
    ],
    workflow: [
      "Receive Engagement Invite from University",
      "Review Schedule & Accept Request",
      "Deliver Live Sessions & Upload Slide Decks",
      "Receive Student Ratings"
    ],
    permissions: {
      allowed: ["pop", "admin"],
      restricted: ["student", "faculty", "university", "tpo"],
      dependencies: "Approved Vetting Status."
    },
    tips: "Add real-world case studies to your masterclass descriptions to increase institutional booking rates.",
    bestPractices: "Upload session slides at least 2 hours prior to live webinars.",
    commonErrors: [
      { error: "Schedule Conflict", resolution: "Update your Availability Calendar in Profile Settings to block busy work hours." }
    ],
    faqs: [
      { q: "Can I accept multiple engagements?", a: "Yes, you can accept as many engagements as your schedule permits." }
    ]
  },

  // --- SHARED / COMMON MODULES ---
  {
    key: "discussions",
    title: "Global Community Discussions & Peer Groups",
    category: "Community & Messaging",
    icon: "MessageSquare",
    allowedRoles: ["student", "faculty", "university", "tpo", "pop", "admin"],
    route: "/discussions",
    overview: "Collaborative social feed and peer group platform enabling students, faculty, and experts to post technical questions, share articles, and form study groups.",
    purpose: "Fosters peer-to-peer learning, knowledge sharing, and academic networking across institutional departments.",
    actions: [
      { name: "Create Post", description: "Publishes text, image, PDF, YouTube video, or link preview post." },
      { name: "Like / Comment", description: "Engages with posts in global or group feeds." },
      { name: "Three-Dot Menu (Report)", description: "Opens options menu to Report offensive content or Delete own post." },
      { name: "Create / Join Group", description: "Establishes private or public batch study groups." }
    ],
    forms: [
      { field: "Post Content", type: "Text Area", description: "Supports mentions (@name) and hashtags (#tag)." },
      { field: "Media Attachment", type: "File / URL Picker", description: "Attaches Image, PDF document, or YouTube link." }
    ],
    tables: [
      { name: "Group Members Roster", columns: ["Member Name", "Role", "User Type", "Joined Date", "Actions"], features: ["Remove Member (Admin)", "Group Delete"] }
    ],
    charts: [],
    workflow: [
      "Navigate to Discussions",
      "Select Global Feed or Specific Group",
      "Compose Post or Comment with Media/Mentions",
      "Engage via Likes, Replies, or Report Menu"
    ],
    permissions: {
      allowed: ["student", "faculty", "university", "tpo", "pop", "admin"],
      restricted: [],
      dependencies: "Authenticated user account."
    },
    tips: "Use hashtags like #Python or #Placement to make your posts easily discoverable in feed searches.",
    bestPractices: "Keep discussion posts respectful and professional; inappropriate content will be reported to Admins.",
    commonErrors: [
      { error: "Invalid YouTube URL", resolution: "Paste standard YouTube watch links (e.g., https://youtube.com/watch?v=...)." }
    ],
    faqs: [
      { q: "Who can see private group posts?", a: "Only approved members of that specific private group can view posts and comments." }
    ]
  },
  {
    key: "direct_chat",
    title: "Real-Time Direct Messaging & Directory",
    category: "Community & Messaging",
    icon: "Send",
    allowedRoles: ["student", "faculty", "university", "tpo", "pop", "admin"],
    route: "/chat",
    overview: "One-on-one real-time chat interface connecting users across roles for direct guidance, project discussions, and academic inquiries.",
    purpose: "Facilitates instant direct communication between students, mentors, faculty, and industry experts.",
    actions: [
      { name: "Select User from Directory", description: "Starts a new 1-on-1 direct conversation." },
      { name: "Send Text / Attachment", description: "Delivers instant message to recipient." },
      { name: "Report Message", description: "Flags inappropriate direct messages for Admin moderation review." },
      { name: "Delete Message", description: "Deletes sent message from conversation history." }
    ],
    forms: [
      { field: "Message Box", type: "Text Input", description: "Instant message content input." }
    ],
    tables: [],
    charts: [],
    workflow: [
      "Open Chat page",
      "Select contact from Directory or Recent Chat List",
      "Type message and press Enter or click Send",
      "Messages sync instantly via backend APIs"
    ],
    permissions: {
      allowed: ["student", "faculty", "university", "tpo", "pop", "admin"],
      restricted: [],
      dependencies: "Must be logged in."
    },
    tips: "Check user online status indicators in the top contact header.",
    bestPractices: "Maintain professional communication standards in direct messages.",
    commonErrors: [
      { error: "Message Delivery Failed", resolution: "Check internet connection; backend automatically retries sending." }
    ],
    faqs: [
      { q: "Can I block a user from messaging me?", a: "Use the Report button to flag unwanted contact to Administrators." }
    ]
  },
  {
    key: "ai_agents",
    title: "EdVergenceX AI Career & Learning Agents",
    category: "AI & Intelligence",
    icon: "Bot",
    allowedRoles: ["student", "faculty", "university", "admin"],
    route: "/ai-agents",
    overview: "Suite of specialized AI subagents providing instant resume scoring, mock interview practice, automated code review, and personalized career roadmaps.",
    purpose: "Harnesses artificial intelligence to deliver 24/7 personalized mentorship and skill evaluation for students.",
    actions: [
      { name: "Select AI Specialist", description: "Chooses AI agent type (Resume Doctor, Interviewer, Code Reviewer, Career Coach)." },
      { name: "Prompt AI", description: "Sends questions, code snippets, or goals to the AI model." },
      { name: "Download AI Feedback Report", description: "Saves generated PDF evaluation report." }
    ],
    forms: [
      { field: "Resume Upload", type: "PDF Upload", description: "Uploads resume for AI ATS score analysis." },
      { field: "Prompt Box", type: "Text Input", description: "Interactive conversation prompt." }
    ],
    tables: [],
    charts: [
      { name: "AI Resume & Interview Scorecard", metrics: ["ATS Compatibility %", "Technical Depth", "Soft Skills", "Keyword Match"], calculation: "NLP semantic evaluation against target job roles.", interpretation: "Identifies specific areas to improve before campus drives." }
    ],
    workflow: [
      "Select AI Agent Tool",
      "Provide Input (Resume PDF, Target Job Role, or Code)",
      "AI Performs Real-Time Analysis",
      "Review Interactive Feedback & Download Recommendations"
    ],
    permissions: {
      allowed: ["student", "faculty", "university", "admin"],
      restricted: [],
      dependencies: "Active user account."
    },
    tips: "Provide specific job descriptions when using the AI Interviewer for tailored questions.",
    bestPractices: "Re-analyze your resume after making suggested skill edits to verify score improvement.",
    commonErrors: [
      { error: "PDF Unreadable Error", resolution: "Ensure PDF text is selectable (not a scanned image format)." }
    ],
    faqs: [
      { q: "Is AI feedback private?", a: "Yes, all AI conversations and uploads are strictly private to your user account." }
    ]
  }
];

/**
 * Dynamic Dynamic Route & Component Introspection Engine
 * Inspects any unlisted or newly added application routes dynamically,
 * ensuring the User Manual never breaks when developers add new features.
 */
export function getAvailableModulesForRole(userRole = "") {
  const normalizedRole = (userRole || "").toLowerCase().trim();
  const effectiveRole = ["system_admin"].includes(normalizedRole) ? "admin" : normalizedRole;

  // Filter modules based on RBAC rules
  const accessibleModules = MODULE_REGISTRY.filter((mod) => {
    if (effectiveRole === "admin") return true; // Admins access all modules
    if (!mod.allowedRoles || mod.allowedRoles.length === 0) return true;
    return mod.allowedRoles.includes(effectiveRole);
  });

  return accessibleModules;
}

/**
 * Perform live dynamic search across module titles, categories, actions, forms, tables, and FAQs
 */
export function searchUserManual(modules, query = "") {
  if (!query || !query.trim()) return modules;

  const q = query.toLowerCase().trim();

  return modules.filter((mod) => {
    // Match in main fields
    if (mod.title.toLowerCase().includes(q)) return true;
    if (mod.category.toLowerCase().includes(q)) return true;
    if (mod.overview.toLowerCase().includes(q)) return true;
    if (mod.purpose.toLowerCase().includes(q)) return true;
    if (mod.route.toLowerCase().includes(q)) return true;

    // Match in actions
    if (mod.actions && mod.actions.some((a) => a.name.toLowerCase().includes(q) || a.description.toLowerCase().includes(q))) return true;

    // Match in forms
    if (mod.forms && mod.forms.some((f) => f.field.toLowerCase().includes(q) || f.description.toLowerCase().includes(q))) return true;

    // Match in tables
    if (mod.tables && mod.tables.some((t) => t.name.toLowerCase().includes(q))) return true;

    // Match in FAQs
    if (mod.faqs && mod.faqs.some((faq) => faq.q.toLowerCase().includes(q) || faq.a.toLowerCase().includes(q))) return true;

    return false;
  });
}

/**
 * Introspects unknown dynamic routes on-the-fly for future-proof auto-documentation
 */
export function generateDynamicFallbackModule(pathname, userRole) {
  const cleanName = pathname.replace(/^\//, "").replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

  return {
    key: `custom_${pathname.replace(/[^a-z0-9]/gi, "_")}`,
    title: `${cleanName} Module`,
    category: "System Feature",
    icon: "Layers",
    allowedRoles: [userRole],
    route: pathname,
    overview: `Auto-generated documentation section for the ${cleanName} feature dynamically detected on route ${pathname}.`,
    purpose: `Provides ${cleanName} capabilities tailored for ${userRole.toUpperCase()} users.`,
    actions: [
      { name: "View Content", description: "Renders primary data view for this route." },
      { name: "Interact", description: "Performs available module actions and form submissions." }
    ],
    forms: [
      { field: "Standard Inputs", type: "Dynamic Form", description: "Form fields associated with this feature." }
    ],
    tables: [
      { name: `${cleanName} Data Table`, columns: ["ID", "Item Name", "Date", "Status"], features: ["Sort", "Filter", "Search"] }
    ],
    charts: [],
    workflow: [`Navigate to ${cleanName}`, "Perform required operational steps", "Submit or Save changes"],
    permissions: {
      allowed: [userRole, "admin"],
      restricted: [],
      dependencies: "Role authentication."
    },
    tips: "Ensure all required form fields are completed before submission.",
    bestPractices: "Keep user data updated regularly.",
    commonErrors: [{ error: "Page Load Error", resolution: "Verify network connectivity and session authentication." }],
    faqs: [{ q: `What is ${cleanName}?`, a: `It is an integrated feature on the EdVergenceX platform.` }]
  };
}
